import { type FC, useCallback, useState } from "react";
import { ActionIcon, Menu, type MenuProps } from "@mantine/core";
import { IconCopy, IconDotsVertical, IconShare, IconTrashFilled, IconPencil } from "@tabler/icons-react";
import { FormattedMessage, useIntl } from "react-intl";
import { useHandleStyleCreation } from "../../../hooks/styleActions.js";
import { useDisclosure } from "@mantine/hooks";
import { DeleteStyleModal } from "../map/components/DeleteStyleModal.js";
import { ShareMapModal } from "../map/components/ShareMapModal.js";
import { RenameMapModal } from "../map/components/RenameMapModal.js";
import type { MapItemControlsProps } from "../map/components/MapItemControls.js";
import { useMapkaNotification } from "../../../hooks/notifications.js";
import { stopPropagation } from "../../../utils/stopPropagation.js";
import clsx from "clsx";
import { active } from "./MapMenu.module.css";

type MapMenuProps = MapItemControlsProps & MenuProps & {
  showShare?: boolean;
};

export const MapMenu: FC<MapMenuProps> = (props) => {
  const { style, version, account, showShare, ...menuStylingProps } = props;
  const {
    id,
    displayName,
    scope,
    sourceStyleId,
  } = style;

  const intl = useIntl();
  const [menuOpened, setMenuOpened] = useState(false);
  const mapkaNotification = useMapkaNotification();

  const notifyAboutDuplicationError = useCallback((errorMessage: string) => {
    if (errorMessage.includes("Style limit exceeded")) {
      mapkaNotification({
        title: intl.formatMessage({
          defaultMessage: "You're at your limit",
          id: "notification.styleLimitExceededDuplication",
        }),
        message: intl.formatMessage({
          defaultMessage: "You're at your maximum number of maps for this tier. To duplicate this map, please upgrade your subscription.",
          id: "notification.styleLimitExceededDuplicationMessage",
        }),
        variant: "warning",
      });
    }
    else {
      mapkaNotification({
        title: intl.formatMessage({
          defaultMessage: "Whoops, error!",
          id: "notification.duplicationError",
        }),
        message: intl.formatMessage({
          defaultMessage: "An error occurred while trying to duplicate this map.",
          id: "notification.duplicationErrorMessage",
        }),
        variant: "error",
      });
    }
  }, [intl, mapkaNotification]);

  const { createStyle } = useHandleStyleCreation(notifyAboutDuplicationError);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [shareModalOpen, { open: openShareModal, close: closeShareModal }] = useDisclosure(false);
  const [renameModalOpened, { open: openRenameModal, close: closeRenameModal }] = useDisclosure(false);
  const optionsMessage = intl.formatMessage({ defaultMessage: "Options", id: "Menu.Options" });

  return (
    <>
      <Menu
        opened={menuOpened}
        onOpen={() => {
          setMenuOpened(true);
        }}
        onClose={() => {
          setMenuOpened(false);
        }}
        {...menuStylingProps}
      >
        <Menu.Target>
          <ActionIcon
            size={32}
            variant="transparent"
            onClick={stopPropagation}
            aria-label={optionsMessage}
            className={clsx({ [active]: menuOpened })}
          >
            <IconDotsVertical size={20} />
          </ActionIcon>
        </Menu.Target>

        <Menu.Dropdown onClick={stopPropagation}>
          {style.scope === "private" && (
            <Menu.Item
              leftSection={<IconPencil size={16} />}
              onClick={openRenameModal}
            >
              <FormattedMessage defaultMessage="Rename" id="label.rename" />
            </Menu.Item>
          )}

          <Menu.Item
            leftSection={<IconCopy size={16} />}
            onClick={() => {
              createStyle({ displayName, sourceStyleId: sourceStyleId ?? id });
            }}
          >
            <FormattedMessage defaultMessage="Duplicate" id="label.duplicate" />
          </Menu.Item>
          {showShare && (
            <Menu.Item
              leftSection={<IconShare size={16} />}
              onClick={openShareModal}
            >
              <FormattedMessage defaultMessage="Share" id="label.share" />
            </Menu.Item>
          )}

          {scope === "private" && (
            <Menu.Item
              leftSection={<IconTrashFilled size={16} />}
              onClick={openDeleteModal}
              c="red.6"
            >
              <FormattedMessage defaultMessage="Delete" id="label.delete" />
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>

      <ShareMapModal
        opened={shareModalOpen}
        onClose={closeShareModal}
        style={style}
        version={version}
        accountName={account.name}
      />

      <DeleteStyleModal
        open={deleteModalOpened}
        onClose={closeDeleteModal}
        style={style}
      />

      <RenameMapModal
        opened={renameModalOpened}
        onClose={closeRenameModal}
        currentName={displayName}
        styleId={id}
      />
    </>
  );
};
